import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Briefcase,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  Wallet,
  ArrowRight,
  Hammer,
  FileText,
  AlertTriangle,
  Send,
  GitPullRequestArrow,
  ReceiptText,
  CalendarClock,
  Megaphone,
  HeartHandshake,
  FileSignature,
  Landmark,
  FileDown,
  Mail,
  Loader2,
  HandCoins,
  Umbrella,
  Timer,
  ShieldAlert,
  NotebookPen,
  ClipboardList,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { isMobileDevice } from "@/lib/device";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { api, API, getToken, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { aud, audFull, fmtDate, JOB_STATUS_META } from "@/lib/format";
import { CHART, axisTick, tooltipStyle, fmtMonthTick, fmtMonthLabel } from "@/lib/chartColors";
import { useAuth } from "@/context/AuthContext";
import { WeatherCard } from "@/components/common/WeatherCard";
import { EmailCombobox } from "@/components/common/EmailCombobox";
import { AttentionBoard, BleedingStagesTicker, CATEGORY, SEVERITY } from "@/components/dashboard/AttentionBoard";
import { DeliveriesWeekStrip } from "@/components/dashboard/DeliveriesWeekStrip";


const ChecklistChip = ({ done, label }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
      done ? "bg-emerald-500/15 text-emerald-300" : "bg-muted text-muted-foreground"
    }`}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${done ? "bg-emerald-400" : "bg-muted-foreground/40"}`} />
    {label}
  </span>
);

const AttentionRow = ({ icon: Icon, iconCls, children, to, testId, action }) => (
  <Link
    to={to}
    className="group flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-muted/70"
    data-testid={testId}
  >
    <Icon className={`h-4 w-4 shrink-0 ${iconCls}`} />
    <span className="min-w-0 flex-1 truncate text-sm">{children}</span>
    {action}
    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
  </Link>
);

/** Email the development costs PDF straight to the development entity. */
const EmailDevReportDialog = ({ job, onOpenChange }) => {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setTo("");
    setMessage("");
    setSending(false);
  }, [job?.job_id]);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post(`/jobs/${job.job_id}/dev-costs-report/email`, { to: to.trim(), message });
      toast.success(`Development costs report sent to ${res.data.to}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!job} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dev-report-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" /> Email development costs report
          </DialogTitle>
          <DialogDescription>
            {job?.job_name} — the branded PDF (costs, billing and net position) is attached to the email and
            saved to the job&apos;s documents.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="dev-report-to" className="text-xs">Recipient email</Label>
            <EmailCombobox
              inputId="dev-report-to"
              placeholder="accounts@developmententity.com.au"
              value={to}
              onChange={setTo}
              testId="dev-report-email-input"
            />
            <p className="text-[11px] text-muted-foreground">Start typing to search saved client and entity contacts — any address works.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dev-report-msg" className="text-xs">Message (optional)</Label>
            <Textarea
              id="dev-report-msg"
              rows={3}
              placeholder="A short note to go above the summary…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="dev-report-message-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="dev-report-cancel-button">
            Cancel
          </Button>
          <Button size="sm" onClick={send} disabled={sending || !to.trim()} data-testid="dev-report-send-button">
            {sending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Send report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AGING_ROWS = [
  { key: "current", label: "Not yet due" },
  { key: "d7", label: "1–7 days overdue" },
  { key: "d14", label: "8–14 days overdue" },
  { key: "d30", label: "15–30 days overdue" },
  { key: "d30_plus", label: "30+ days overdue" },
];

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [sendingPack, setSendingPack] = useState(null);
  const [emailReportJob, setEmailReportJob] = useState(null);
  const [varAlerts, setVarAlerts] = useState([]);
  const [marginAlerts, setMarginAlerts] = useState([]);
  const [marginGuard, setMarginGuard] = useState(null);
  const [liabilities, setLiabilities] = useState(null);
  const [payPlan, setPayPlan] = useState(null);
  const [budgetHealth, setBudgetHealth] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const cashflowRef = useRef(null);

  // Already-signed-in staff opening the app on a phone land in Field View once
  // per session (the Field View "exit" button still brings them back here).
  useEffect(() => {
    if (user && isMobileDevice() && user.role !== "contractor" && !sessionStorage.getItem("g5_field_autoredirect")) {
      sessionStorage.setItem("g5_field_autoredirect", "1");
      navigate("/field", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data)).catch(() => {});
    api.get("/financials/variance-alerts").then((res) => setVarAlerts(res.data.variances || [])).catch(() => {});
    api.get("/financials/margin-alerts").then((res) => setMarginAlerts(res.data.alerts || [])).catch(() => {});
    api.get("/financials/margin-guard").then((res) => setMarginGuard(res.data)).catch(() => {});
    api.get("/liabilities/upcoming").then((res) => setLiabilities(res.data)).catch(() => {});
    api.get("/liabilities/payment-plan").then((res) => setPayPlan(res.data)).catch(() => {});
    api.get("/financials/budget-health").then((res) => setBudgetHealth(res.data)).catch(() => {});
  }, []);

  const refreshDashboard = () => api.get("/dashboard").then((res) => setData(res.data)).catch(() => {});

  // Clear a "Critical site delay" row (the diary entry itself is untouched)
  const [dismissingDelay, setDismissingDelay] = useState(null);
  const dismissCriticalDelay = async (e, d) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissingDelay(d.id);
    try {
      await api.post(`/site-diary/${d.id}/alert/dismiss`, {});
      toast.success("Critical delay alert cleared");
      refreshDashboard();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDismissingDelay(null);
    }
  };

  // One-click welcome pack send straight from the nudge row
  const sendWelcomePack = async (e, w) => {
    e.preventDefault();
    e.stopPropagation();
    setSendingPack(w.job_id);
    try {
      const res = await api.post(`/jobs/${w.job_id}/welcome-pack/email`, { extra_emails: [], note: "" });
      toast.success(`Welcome pack on its way to ${res.data.recipients.join(", ")}`);
      refreshDashboard();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSendingPack(null);
    }
  };

  if (!data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const { kpis, attention, aging, live_jobs, quote_jobs, upcoming_bookings, cashflow_timeline } = data;
  const devCosts = data.dev_costs;
  const attentionCount =
    (attention?.overdue_invoices?.length || 0) +
    (attention?.broken_promises?.length || 0) +
    (attention?.overdue_bills?.length || 0) +
    (attention?.budget_overruns?.length || 0) +
    (attention?.awaiting_quotes?.length || 0) +
    (attention?.pending_variations?.length || 0) +
    (attention?.stages_ready?.length || 0) +
    (attention?.cash_warnings?.length || 0) +
    (attention?.open_field_issues?.length || 0) +
    (attention?.critical_delays?.length || 0) +
    (attention?.client_updates_due?.length || 0) +
    (attention?.welcome_packs_due?.length || 0) +
    (attention?.scopes_accepted?.length || 0) +
    (varAlerts || []).filter((v) => v.variance > 0).length +
    (marginAlerts || []).length +
    (marginGuard?.flagged || []).length;
  const nextBooking = upcoming_bookings?.[0];
  const maxAging = Math.max(...AGING_ROWS.map((r) => aging?.[r.key] || 0), 1);

  const kpiCards = [
    {
      label: "Active jobs",
      value: kpis.active_jobs,
      sub: `${kpis.total_jobs} total`,
      icon: Briefcase,
      onClick: () => navigate("/jobs"),
      testId: "dashboard-kpi-active-jobs",
    },
    {
      label: "Pipeline value",
      value: aud(kpis.pipeline_value),
      sub: `Awaiting response ${aud(kpis.awaiting_response_value)} · Contracted ${aud(kpis.contracted_value)}`,
      icon: TrendingUp,
      onClick: () => navigate("/jobs"),
      testId: "dashboard-kpi-pipeline",
    },
    {
      label: "Upcoming bookings",
      value: kpis.upcoming_bookings,
      sub: nextBooking
        ? `Next: ${nextBooking.contractor_name || nextBooking.trade || "booking"} · ${fmtDate(nextBooking.start_date)}`
        : "Next 14 days",
      icon: CalendarDays,
      onClick: () => navigate("/schedule"),
      testId: "dashboard-kpi-bookings",
    },
    {
      label: "Cash position",
      value: aud(kpis.cash_position),
      sub: `Outstanding ${aud(aging?.total_outstanding || 0)}`,
      icon: Wallet,
      onClick: () => cashflowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      testId: "dashboard-kpi-cash",
    },
  ];

  // Group "needs attention" items into per-site cards. Every alert carries a
  // category (Financials / Site Operations / Admin & Comms) and a severity
  // (critical → warning → info) so the board can fold quiet sites and flag
  // the noisy ones with colour-coded pills.
  //   critical: money overdue, budget/margin breached, cash danger
  //   warning:  slipping (broken promise, long-pending approvals, open site issues)
  //   info:     routine action (claim a stage, send an update, raise a PO)
  const { FINANCIALS, OPERATIONS, ADMIN } = CATEGORY;
  const { CRITICAL, WARNING, INFO } = SEVERITY;
  const attentionGroups = (() => {
    const groups = new Map();
    const add = (jobId, jobName, node, category, severity, weight = 1) => {
      const key = jobId || "__general";
      if (!groups.has(key)) groups.set(key, { jobId: jobId || null, jobName: jobName || "", items: [] });
      const g = groups.get(key);
      if (!g.jobName && jobName) g.jobName = jobName;
      g.items.push({ key: node.key, node, category, severity, weight });
    };
    attention.overdue_invoices.forEach((i) =>
      add(
        i.job_id,
        i.job_name,
        <AttentionRow
          key={i.id}
          icon={AlertTriangle}
          iconCls="text-red-400"
          to={`/jobs/${i.job_id}?tab=payments`}
          testId={`attention-overdue-${i.id}`}
        >
          <strong>{i.invoice_number}</strong> overdue {i.days_overdue}d · {audFull(i.amount_inc_gst)}
        </AttentionRow>,
        FINANCIALS,
        CRITICAL
      )
    );
    (attention.broken_promises || []).forEach((i) =>
      add(
        i.job_id,
        i.job_name,
        <AttentionRow
          key={`bp-${i.id}`}
          icon={CalendarClock}
          iconCls="text-sky-300"
          to={`/cashflow?tab=invoices&job=${i.job_id}`}
          testId={`attention-promise-${i.id}`}
        >
          <strong>{i.invoice_number}</strong> promised {fmtDate(i.promised_date)} — {i.days_broken}d past · {audFull(i.balance)} owing
        </AttentionRow>,
        FINANCIALS,
        WARNING
      )
    );
    (attention.overdue_bills || []).forEach((b) =>
      add(
        b.job_id,
        b.job_name,
        <AttentionRow
          key={`ob-${b.id}`}
          icon={ReceiptText}
          iconCls="text-red-400"
          to={`/cashflow?tab=bills${b.job_id ? `&job=${b.job_id}` : ""}`}
          testId={`attention-bill-${b.id}`}
        >
          Bill <strong>{b.bill_number}</strong> from {b.supplier_name} overdue {b.days_overdue}d · {audFull(b.amount_due)} owing
        </AttentionRow>,
        FINANCIALS,
        CRITICAL
      )
    );
    // cost centres whose actuals climbed past the budget, rolled up by build
    // stage so the bleeding stage is obvious (worst first, Unstaged last)
    (() => {
      const byJob = new Map();
      (attention.budget_overruns || []).forEach((o) => {
        const jKey = o.job_id || "__none";
        if (!byJob.has(jKey)) byJob.set(jKey, { jobId: o.job_id, jobName: o.job_name, stages: new Map() });
        const sKey = o.stage_code || "__unstaged";
        const stages = byJob.get(jKey).stages;
        if (!stages.has(sKey))
          stages.set(sKey, {
            code: o.stage_code || "",
            name: o.stage_code ? o.stage_name || "Unstaged" : "Unstaged",
            budget: 0,
            actuals: 0,
            over: 0,
            centres: [],
          });
        const s = stages.get(sKey);
        s.budget += o.budget || 0;
        s.actuals += o.actuals || 0;
        s.over += o.over || 0;
        s.centres.push(o);
      });
      byJob.forEach((jg) => {
        const ordered = [...jg.stages.values()].sort(
          (a, b) => (a.code ? 0 : 1) - (b.code ? 0 : 1) || b.over - a.over
        );
        ordered.forEach((s) => {
          const pct = s.budget > 0 ? Math.round((s.over / s.budget) * 100) : null;
          const slug = String(s.code || "unstaged").replace(/[^a-zA-Z0-9_-]/g, "") || "unstaged";
          add(
            jg.jobId,
            jg.jobName,
            <div
              key={`bo-${jg.jobId}-${slug}`}
              className="rounded-lg border border-red-400/25 bg-red-500/5"
              data-testid={`attention-overrun-stage-${jg.jobId}-${slug}`}
            >
              <Link
                to={`/financials?job=${jg.jobId}&view=bva`}
                className="group flex items-center gap-3 rounded-t-lg px-3 py-2 transition-colors hover:bg-muted/70"
                data-testid={`attention-overrun-stage-link-${jg.jobId}-${slug}`}
              >
                <TrendingDown className="h-4 w-4 shrink-0 text-red-400" />
                <span className="min-w-0 flex-1 truncate text-sm">
                  <strong>{s.name}</strong> over budget —{" "}
                  <span className="font-semibold text-red-400">{audFull(s.over)} over</span>
                  {pct !== null && <span className="text-muted-foreground"> ({pct}% over)</span>}
                </span>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>
              <div className="space-y-0.5 pb-1.5">
                {s.centres.map((o) => (
                  <Link
                    key={`bo-${o.job_id}-${o.code}`}
                    to={`/financials?job=${o.job_id}&view=bva`}
                    className="group flex items-center gap-2 rounded px-3 py-1 pl-10 transition-colors hover:bg-muted/70"
                    data-testid={`attention-overrun-${o.job_id}-${o.code}`}
                  >
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                      {o.code === "UNC" ? "Uncategorised" : `${o.code} ${o.name}`}: spent {aud(o.actuals)} vs budget{" "}
                      {aud(o.budget)} <span className="font-semibold text-red-400">({audFull(o.over)} over)</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>,
            FINANCIALS,
            CRITICAL,
            1 + s.centres.length * 0.6
          );
        });
      });
    })();
    attention.awaiting_quotes.forEach((q) =>
      add(
        q.job_id,
        q.job_name,
        <AttentionRow
          key={q.id}
          icon={Send}
          iconCls="text-amber-400"
          to={`/jobs/${q.job_id}?tab=payments`}
          testId={`attention-quote-${q.id}`}
        >
          Quote <strong>{q.reference}</strong> awaiting response {q.days_waiting}d · {aud(q.total_inc_gst)}
        </AttentionRow>,
        ADMIN,
        q.days_waiting >= 14 ? WARNING : INFO
      )
    );
    attention.pending_variations.forEach((v) =>
      add(
        v.job_id,
        v.job_name,
        <AttentionRow
          key={v.id}
          icon={GitPullRequestArrow}
          iconCls="text-violet-300"
          to={`/jobs/${v.job_id}?tab=variations`}
          testId={`attention-variation-${v.id}`}
        >
          Variation <strong>{v.reference}</strong> pending approval {v.days_waiting}d
        </AttentionRow>,
        ADMIN,
        v.days_waiting >= 14 ? WARNING : INFO
      )
    );
    attention.stages_ready.forEach((s) =>
      add(
        s.job_id,
        s.job_name,
        <AttentionRow
          key={`${s.job_id}-${s.stage_id}`}
          icon={ReceiptText}
          iconCls="text-primary"
          to={`/jobs/${s.job_id}?tab=payments`}
          testId={`attention-stage-${s.stage_id}`}
        >
          <strong>{s.stage_name}</strong> stage ready to invoice · {audFull(s.amount_inc_gst)}
        </AttentionRow>,
        FINANCIALS,
        INFO
      )
    );
    (attention.open_field_issues || []).forEach((fi) =>
      add(
        fi.job_id,
        fi.job_name,
        <AttentionRow key={`fi-${fi.id}`} icon={Hammer} iconCls="text-orange-400" to="/field" testId={`attention-issue-${fi.id}`}>
          <strong>{fi.raised_by_contractor ? "Contractor-raised issue" : "Site issue"}</strong>{" "}
          {fi.status === "ready_for_review" ? "ready for review" : `open ${fi.days_open}d`}
          {fi.items_n > 1 ? ` · ${fi.items_n} items` : ""}
          {fi.contractor_name ? ` · ${fi.contractor_name}` : ""}
        </AttentionRow>,
        OPERATIONS,
        fi.status === "ready_for_review" ? INFO : WARNING
      )
    );
    // critical delays logged in the Daily Site Diary — stay until the entry is downgraded or the alert is cleared
    (attention.critical_delays || []).forEach((d) =>
      add(
        d.job_id,
        d.job_name,
        <AttentionRow
          key={`cd-${d.id}`}
          icon={NotebookPen}
          iconCls="text-red-400"
          to={`/site-diary?job=${d.job_id}&entry=${d.id}`}
          testId={`attention-critical-delay-${d.id}`}
          action={
            <Button
              size="sm"
              variant="outline"
              className="h-6 shrink-0 px-2 text-[11px]"
              disabled={dismissingDelay === d.id}
              onClick={(e) => dismissCriticalDelay(e, d)}
              data-testid={`attention-critical-delay-dismiss-${d.id}`}
            >
              {dismissingDelay === d.id ? "Clearing…" : "Clear"}
            </Button>
          }
        >
          <strong>Critical site delay</strong> {fmtDate(d.date)} — {d.excerpt}
          {d.created_by_name ? ` · ${d.created_by_name}` : ""}
        </AttentionRow>,
        OPERATIONS,
        CRITICAL
      )
    );
    // open punch-list defects (Phase 87) — one row per project; critical items block the final progress claim
    (attention.open_defects || []).forEach((od) =>
      add(
        od.job_id,
        od.job_name,
        <AttentionRow
          key={`od-${od.job_id}`}
          icon={ClipboardList}
          iconCls={od.critical ? "text-red-400" : od.awaiting_verification || od.overdue ? "text-amber-400" : "text-sky-300"}
          to={`/jobs/${od.job_id}?tab=defects`}
          testId={`attention-defects-${od.job_id}`}
        >
          <strong>{od.open} open defect{od.open === 1 ? "" : "s"}</strong>
          {od.critical ? ` · ${od.critical} critical — final claim blocked` : ""}
          {od.awaiting_verification ? ` · ${od.awaiting_verification} to verify` : ""}
          {od.overdue ? ` · ${od.overdue} overdue` : ""}
        </AttentionRow>,
        OPERATIONS,
        od.severity === "critical" ? CRITICAL : od.severity === "warning" ? WARNING : INFO
      )
    );
    // formal defect notices past their response date with unresolved items (Phase 89)
    (attention.overdue_notices || []).forEach((on) =>
      add(
        on.job_id,
        on.job_name,
        <AttentionRow
          key={`on-${on.notice_id}`}
          icon={FileSignature}
          iconCls={on.severity === "critical" ? "text-red-400" : "text-amber-400"}
          to={`/notices?job=${on.job_id}&open=${on.notice_id}`}
          testId={`attention-notice-${on.notice_id}`}
        >
          <strong>Defect notice {on.number} overdue</strong> · {on.trade} · {on.unresolved} of {on.total} unresolved · {on.days_overdue} day{on.days_overdue === 1 ? "" : "s"} past {on.response_due ? new Date(`${on.response_due}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "due"}
        </AttentionRow>,
        OPERATIONS,
        on.severity === "critical" ? CRITICAL : WARNING
      )
    );
    (attention.client_updates_due || []).forEach((u) =>
      add(
        u.job_id,
        u.job_name,
        <AttentionRow
          key={`cu-${u.job_id}`}
          icon={Megaphone}
          iconCls="text-sky-300"
          to={`/jobs/${u.job_id}?tab=client-updates`}
          testId={`attention-client-update-${u.job_id}`}
        >
          <strong>Client update due</strong> {u.never_sent ? "— none sent yet" : `— last sent ${u.days_since}d ago`}
        </AttentionRow>,
        ADMIN,
        INFO
      )
    );
    (attention.welcome_packs_due || []).forEach((w) =>
      add(
        w.job_id,
        w.job_name,
        <AttentionRow
          key={`wp-${w.job_id}`}
          icon={HeartHandshake}
          iconCls="text-primary"
          to={`/jobs/${w.job_id}?welcome=1`}
          testId={`attention-welcome-pack-${w.job_id}`}
          action={
            w.has_client_email ? (
              <Button
                size="sm"
                variant="outline"
                className="h-6 shrink-0 border-primary/40 px-2 text-[11px] text-primary hover:bg-primary/10"
                disabled={sendingPack === w.job_id}
                onClick={(e) => sendWelcomePack(e, w)}
                data-testid={`attention-welcome-pack-send-${w.job_id}`}
              >
                <Send className="mr-1 h-3 w-3" />
                {sendingPack === w.job_id ? "Sending…" : "Send now"}
              </Button>
            ) : null
          }
        >
          <strong>Welcome pack to send</strong> — contract signed {fmtDate(w.signed_date)}
          {w.client_name ? ` · ${w.client_name}` : ""}
          {!w.has_client_email ? " (no client email yet)" : ""}
        </AttentionRow>,
        ADMIN,
        INFO
      )
    );
    (attention.scopes_accepted || []).forEach((s) =>
      add(
        s.job_id,
        s.job_name,
        <AttentionRow
          key={`sow-${s.id}`}
          icon={FileSignature}
          iconCls="text-emerald-400"
          to={`/jobs/${s.job_id}?tab=contracts`}
          testId={`attention-sow-${s.id}`}
        >
          <strong>Scope accepted</strong> — {s.name}
          {s.ack_name ? ` by ${s.ack_name}` : ""} {s.days_since > 0 ? `${s.days_since}d ago` : "today"} · raise the PO
        </AttentionRow>,
        ADMIN,
        INFO
      )
    );
    (attention.cash_warnings || []).forEach((w, i) => {
      const hasJob = w.code === "invoice_prep" && w.job_id;
      const sep = (w.title || "").lastIndexOf(" — ");
      const jobName = hasJob && sep > -1 ? w.title.slice(sep + 3) : "";
      const title = hasJob && sep > -1 ? w.title.slice(0, sep) : w.title;
      add(
        hasJob ? w.job_id : null,
        jobName,
        <AttentionRow
          key={`cw-${w.code}-${w.stage_id || i}`}
          icon={w.level === "action" ? ReceiptText : AlertTriangle}
          iconCls={w.level === "danger" ? "text-red-400" : w.level === "action" ? "text-primary" : "text-amber-400"}
          to={hasJob ? `/jobs/${w.job_id}?tab=payments` : "/cashflow"}
          testId={`attention-cash-${w.code}-${i}`}
        >
          <strong>{title}</strong>
        </AttentionRow>,
        FINANCIALS,
        w.level === "danger" ? CRITICAL : w.level === "action" ? INFO : WARNING
      );
    });
    // daily cost-variance watch (spend vs budget, company-wide)
    (varAlerts || []).forEach(
      (v) =>
        v.variance > 0 &&
        add(
          null,
          "",
          <AttentionRow
            key={`var-${v.code}`}
            icon={AlertTriangle}
            iconCls="text-red-400"
            to="/financials"
            testId={`attention-variance-${v.code}`}
          >
            <strong>Cost variance watch</strong> — {v.code} {v.name}: spend {aud(v.spend)} vs budget {aud(v.budget)} (+{v.pct}%)
          </AttentionRow>,
          FINANCIALS,
          CRITICAL
        )
    );
    // Low Margin Risk flags raised when a takeoff budget exceeds the original quote
    (marginAlerts || []).forEach((m) =>
      add(
        m.job_id,
        m.job_name,
        <AttentionRow
          key={`margin-${m.job_id}`}
          icon={AlertTriangle}
          iconCls="text-red-400"
          to={`/jobs/${m.job_id}?tab=estimate`}
          testId={`attention-margin-${m.job_id}`}
        >
          <strong>Low Margin Risk</strong> — estimated cost {aud(m.margin?.total_estimated_cost)} vs quote{" "}
          {aud(m.margin?.original_quote)}
        </AttentionRow>,
        FINANCIALS,
        CRITICAL
      )
    );
    // Margin Guard — projected margin (contract vs higher of budget/actuals)
    // has slipped below the configurable warning threshold
    (marginGuard?.flagged || []).forEach((m) =>
      add(
        m.job_id,
        m.job_name,
        <AttentionRow
          key={`mguard-${m.job_id}`}
          icon={ShieldAlert}
          iconCls="text-amber-300"
          to={`/financials?job=${m.job_id}&view=bva`}
          testId={`attention-margin-guard-${m.job_id}`}
        >
          <strong>Margin Guard</strong> — projected margin{" "}
          <span className={m.margin_pct < 0 ? "font-semibold text-red-400" : "font-semibold text-amber-300"}>
            {m.margin_pct}%
          </span>{" "}
          is below your {marginGuard?.settings?.threshold_pct ?? 15}% line · projected profit{" "}
          {audFull(m.projected_margin_ex)} ex GST
        </AttentionRow>,
        FINANCIALS,
        m.margin_pct < 0 ? CRITICAL : WARNING
      )
    );
    return [...groups.values()];
  })();

  const bleedingStages = attention.budget_overruns_by_stage || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Company-wide financial risk ticker — sticky so it never scrolls away */}
      <BleedingStagesTicker stages={bleedingStages} totalOver={attention.budget_overruns_amount} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold">
            G'day, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Here's what needs your attention today.</p>
        </div>
        <Button onClick={() => navigate("/jobs?new=1")} data-testid="dashboard-new-job-button">
          <Hammer className="mr-1.5 h-4 w-4" /> New job
        </Button>
      </div>

      {/* Site weather */}
      <WeatherCard />

      {/* Needs attention — one collapsible card per site, alerts categorised inside */}
      {attentionCount > 0 && <AttentionBoard groups={attentionGroups} jobsIndex={data.jobs_index || []} />}

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpiCards.map(({ label, value, sub, icon: Icon, onClick, testId }) => (
          <Card
            key={label}
            className="min-w-0 cursor-pointer shadow-sm transition-colors hover:border-primary/40"
            onClick={onClick}
            data-testid={testId}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
                  <p className="mt-1.5 font-heading text-2xl font-semibold tabular-nums">{value}</p>
                  <p className="mt-1 truncate text-xs text-muted-foreground" title={sub}>{sub}</p>
                </div>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
                  <Icon className="h-[18px] w-[18px]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Phase 133 — supplier deliveries landing this week (every job), coloured by confirmed time slot */}
      <DeliveriesWeekStrip />

      {/* Live jobs + Jobs to quote */}
      <div className="grid min-w-0 gap-4 lg:grid-cols-2" data-testid="dashboard-jobs-grid">
        <Card className="min-w-0 shadow-sm" data-testid="dashboard-live-jobs-card">
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Hammer className="h-4 w-4 text-primary" /> Live jobs
            </CardTitle>
            <Link to="/jobs" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              All jobs <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {live_jobs.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">No jobs on the tools right now.</p>
            )}
            {live_jobs.map((job) => {
              const meta = JOB_STATUS_META[job.status] || JOB_STATUS_META.quoting;
              return (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-muted/50"
                  data-testid={`dashboard-live-job-${job.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{job.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {job.outstanding_to_invoice != null
                        ? `${aud(job.outstanding_to_invoice)} left to invoice${job.next_stage ? ` · next: ${job.next_stage}` : ""}`
                        : job.client_name || job.address || "—"}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
                    {job.contract_value ? aud(job.contract_value) : job.latest_estimate ? aud(job.latest_estimate) : ""}
                  </span>
                  <Badge variant="outline" className={meta.cls}>
                    {meta.label}
                  </Badge>
                </Link>
              );
            })}
          </CardContent>
        </Card>

        <Card className="min-w-0 shadow-sm" data-testid="dashboard-quote-jobs-card">
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <FileText className="h-4 w-4 text-primary" /> Jobs to quote
            </CardTitle>
            <Link to="/jobs" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              All jobs <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {quote_jobs.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">Nothing waiting to be quoted.</p>
            )}
            {quote_jobs.map((job) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-muted/50"
                data-testid={`dashboard-quote-job-${job.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{job.name}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <ChecklistChip done={job.has_details} label="Details" />
                    <ChecklistChip done={job.files_count > 0} label={`Plans${job.files_count ? ` (${job.files_count})` : ""}`} />
                    <ChecklistChip done={job.has_takeoff} label="Takeoff" />
                  </div>
                </div>
                <span className="text-sm font-medium tabular-nums text-muted-foreground">
                  {job.latest_estimate ? aud(job.latest_estimate) : ""}
                </span>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Money — two self-sizing stacks so neither column stretches to leave dead space:
          left (2/3): cashflow chart, budget health, development costs · right (1/3): payables + receivables */}
      <div ref={cashflowRef} className="grid gap-4 lg:grid-cols-3" data-testid="dashboard-money-grid">
        {/* The chart card is flex-1: it grows to absorb whatever height the right-hand stack
            needs, so the two columns always end on the same line with no dead space. */}
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2" data-testid="dashboard-money-left">
        <Card className="flex flex-1 flex-col shadow-sm" data-testid="dashboard-cashflow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Cashflow — last 6 months</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            {!cashflow_timeline.some((m) => m.income || m.expenses || m.invoiced) ? (
              <p className="flex flex-1 items-center justify-center py-10 text-center text-sm text-muted-foreground">
                No money movement yet — invoices and expenses will show here.
              </p>
            ) : (
              <div className="min-h-[18rem] flex-1" data-testid="dashboard-cashflow-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={cashflow_timeline} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                    <XAxis dataKey="month" tickFormatter={fmtMonthTick} tick={axisTick(12)} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => aud(v)} tick={axisTick(11)} axisLine={false} tickLine={false} width={80} />
                    <Tooltip
                      formatter={(v, name) => [audFull(v), name]}
                      labelFormatter={fmtMonthLabel}
                      cursor={{ fill: "hsl(var(--muted) / 0.6)" }}
                      contentStyle={tooltipStyle}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
                    <Bar dataKey="invoiced" name="Invoiced" fill={CHART.invoiced} radius={[3, 3, 0, 0]} maxBarSize={34} />
                    <Bar dataKey="income" name="Received" fill={CHART.received} radius={[3, 3, 0, 0]} maxBarSize={34} />
                    <Bar dataKey="expenses" name="Expenses" fill={CHART.expenses} radius={[3, 3, 0, 0]} maxBarSize={34} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

          {/* Budget health — spend vs Master Budget with the contingency buffer visible */}
          {budgetHealth && (budgetHealth.jobs || []).filter((j) => j.budget_total > 0).length > 0 && (
            <Card className="shadow-sm" data-testid="dashboard-budget-health-card">
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Wallet className="h-4 w-4 text-primary" /> Budget health
                </CardTitle>
                <Link
                  to="/financials"
                  className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  data-testid="dashboard-budget-health-link"
                >
                  Master Budget <ArrowRight className="h-3 w-3" />
                </Link>
              </CardHeader>
              <CardContent className="space-y-3.5 pt-1">
                {(budgetHealth.jobs || []).filter((j) => j.budget_total > 0).slice(0, 5).map((j) => {
                  const basePct = (j.base_budget / j.budget_total) * 100;
                  const usedPct = Math.min(100, (j.actuals / j.budget_total) * 100);
                  return (
                    <div key={j.job_id} data-testid={`dashboard-budget-health-${j.job_id}`}>
                      <div className="flex items-baseline justify-between gap-2 text-xs">
                        <p className="min-w-0 flex-1 truncate font-medium">{j.job_name}</p>
                        <p className="tabular-nums text-muted-foreground">
                          <span className={j.over ? "font-semibold text-red-300" : "text-foreground"}>{audFull(j.actuals)}</span>
                          {" of "}
                          {audFull(j.budget_total)}
                        </p>
                      </div>
                      <div className="relative mt-1.5 h-2.5 overflow-hidden rounded-full border border-border/60" title={`Base budget ${audFull(j.base_budget)}${j.contingency > 0 ? ` + contingency ${audFull(j.contingency)}` : ""} · spent ${audFull(j.actuals)}`}>
                        <div className="absolute inset-y-0 left-0 bg-muted" style={{ width: `${basePct}%` }} />
                        {j.contingency > 0 && (
                          <div
                            className="absolute inset-y-0 border-l border-sky-400/50 bg-sky-500/25"
                            style={{ left: `${basePct}%`, width: `${100 - basePct}%` }}
                            data-testid={`dashboard-budget-buffer-${j.job_id}`}
                          />
                        )}
                        <div
                          className={`absolute inset-y-0 left-0 rounded-r-full ${j.over ? "bg-red-400/90" : "bg-emerald-400/80"}`}
                          style={{ width: `${usedPct}%` }}
                        />
                      </div>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {j.contingency > 0 ? (
                          <span className="text-sky-300/90">
                            <Umbrella className="mr-0.5 inline h-2.5 w-2.5" />
                            {audFull(j.contingency)} buffer ({j.contingency_pct}%)
                          </span>
                        ) : (
                          <span>no contingency set</span>
                        )}
                        {" · "}
                        {j.remaining >= 0 ? `${audFull(j.remaining)} left` : `${audFull(Math.abs(j.remaining))} over`}
                      </p>
                    </div>
                  );
                })}
                <div className="flex items-center gap-3 border-t border-border/60 pt-2 text-[10px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-muted ring-1 ring-border" /> Base budget</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-sky-500/40" /> Contingency</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-400/80" /> Spent</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Development costs — billed vs spent across every job */}
          {devCosts && (devCosts.billed > 0 || devCosts.spent > 0) && (
            <Card className="shadow-sm" data-testid="dashboard-dev-costs-card">
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Landmark className="h-4 w-4 text-amber-300" /> Development costs
                </CardTitle>
                <Link
                  to="/financials"
                  className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  data-testid="dashboard-dev-costs-link"
                >
                  Financials <ArrowRight className="h-3 w-3" />
                </Link>
              </CardHeader>
              <CardContent className="space-y-3 pt-1">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg border border-border/70 bg-muted/20 px-2.5 py-2" data-testid="dev-costs-billed">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Billed</p>
                    <p className="mt-0.5 text-sm font-semibold tabular-nums text-emerald-300">{aud(devCosts.billed)}</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 px-2.5 py-2" data-testid="dev-costs-spent">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Spent</p>
                    <p className="mt-0.5 text-sm font-semibold tabular-nums text-amber-300">{aud(devCosts.spent)}</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 px-2.5 py-2" data-testid="dev-costs-net">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Net</p>
                    <p className={`mt-0.5 text-sm font-semibold tabular-nums ${devCosts.net < 0 ? "text-red-300" : "text-emerald-300"}`}>
                      {aud(devCosts.net)}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  {(devCosts.jobs || []).map((j) => (
                    <div key={j.job_id} className="flex items-center gap-1" data-testid={`dev-costs-job-${j.job_id}`}>
                      <Link
                        to={`/financials?job=${j.job_id}`}
                        className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-muted/60"
                      >
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">{j.job_name}</span>
                        <span className="tabular-nums text-emerald-300/90">{aud(j.billed)}</span>
                        <span className="text-muted-foreground/50">/</span>
                        <span className="tabular-nums text-amber-300/90">{aud(j.spent)}</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => setEmailReportJob({ job_id: j.job_id, job_name: j.job_name })}
                        title="Email the development costs report (PDF attached)"
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        data-testid={`dev-costs-email-${j.job_id}`}
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => window.open(`${API}/jobs/${j.job_id}/dev-costs-report?token=${getToken()}`, "_blank")}
                        title="Download the printable development costs report (PDF)"
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        data-testid={`dev-costs-report-${j.job_id}`}
                      >
                        <FileDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Billed (income) vs spent (bills) on development-tagged lines, every job, all time.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-4" data-testid="dashboard-money-right">
          {/* Upcoming liabilities — this month's bills + costs rolled over from previous months */}
          {liabilities && (liabilities.totals?.combined_total || 0) >= 0.01 && (
            <Card className="shadow-sm" data-testid="dashboard-liabilities-card">
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <HandCoins className="h-4 w-4 text-amber-300" /> Upcoming liabilities
                </CardTitle>
                <Link
                  to="/invoice-inbox?tab=unpaid"
                  className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  data-testid="dashboard-liabilities-link"
                >
                  Unpaid tab <ArrowRight className="h-3 w-3" />
                </Link>
              </CardHeader>
              <CardContent className="space-y-3 pt-1">
                <p className="text-xs text-muted-foreground">
                  {liabilities.month_label} —{" "}
                  <span className="font-semibold text-foreground tabular-nums" data-testid="dashboard-liabilities-total">
                    {audFull(liabilities.totals.combined_total)}
                  </span>{" "}
                  to pay
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-orange-500/25 bg-orange-500/[0.07] px-2.5 py-2" data-testid="dashboard-liabilities-rolled">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-orange-300">Rolled over</p>
                    <p className="mt-0.5 text-sm font-semibold tabular-nums text-orange-300">{audFull(liabilities.totals.rolled_total)}</p>
                    <p className="text-[10px] text-muted-foreground">{liabilities.totals.rolled_count} from previous months</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 px-2.5 py-2" data-testid="dashboard-liabilities-current">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">This month</p>
                    <p className="mt-0.5 text-sm font-semibold tabular-nums text-amber-300">{audFull(liabilities.totals.current_total)}</p>
                    <p className="text-[10px] text-muted-foreground">{liabilities.totals.current_count} bill{liabilities.totals.current_count === 1 ? "" : "s"}</p>
                  </div>
                </div>
                {(liabilities.rolled || []).slice(0, 4).map((r) => (
                  <Link
                    key={`${r.kind}-${r.id}`}
                    to="/invoice-inbox?tab=unpaid"
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-muted/60"
                    data-testid={`dashboard-liability-row-${r.id}`}
                  >
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">{r.supplier || "Supplier"}</span>
                    <Badge variant="outline" className="border-orange-500/30 bg-orange-500/10 px-1 py-0 text-[9px] font-normal text-orange-300">
                      <CalendarClock className="mr-0.5 h-2.5 w-2.5" /> {r.rolled_from}
                    </Badge>
                    <span className="tabular-nums text-orange-300/90">{audFull(r.amount_due)}</span>
                  </Link>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  Unpaid costs roll into the current month until marked paid — payment books them to the day you pay.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Payment priorities — pay these first so nothing hits 90 days */}
          {payPlan && (payPlan.totals?.breached_count + payPlan.totals?.this_week_count + payPlan.totals?.this_month_count + payPlan.totals?.later_count) > 0 && (
            <Card className="shadow-sm" data-testid="dashboard-payplan-card">
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Timer className="h-4 w-4 text-amber-300" /> Payment priorities
                </CardTitle>
                <Link
                  to="/invoice-inbox?tab=unpaid"
                  className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  data-testid="dashboard-payplan-link"
                >
                  Unpaid tab <ArrowRight className="h-3 w-3" />
                </Link>
              </CardHeader>
              <CardContent className="space-y-3 pt-1">
                <div className="grid grid-cols-3 gap-2">
                  <div className={`rounded-lg border px-2 py-1.5 text-center ${payPlan.totals.breached_count ? "border-red-500/30 bg-red-500/[0.08]" : "border-border/60 bg-muted/20"}`} data-testid="dashboard-payplan-breached">
                    <p className="text-[9px] uppercase tracking-wide text-muted-foreground">90+ overdue</p>
                    <p className={`text-xs font-semibold tabular-nums ${payPlan.totals.breached_count ? "text-red-300" : "text-muted-foreground/50"}`}>
                      {payPlan.totals.breached_count ? audFull(payPlan.totals.breached_total) : "—"}
                    </p>
                  </div>
                  <div className={`rounded-lg border px-2 py-1.5 text-center ${payPlan.totals.this_week_count ? "border-amber-500/30 bg-amber-500/[0.08]" : "border-border/60 bg-muted/20"}`} data-testid="dashboard-payplan-week">
                    <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Hits 90 this week</p>
                    <p className={`text-xs font-semibold tabular-nums ${payPlan.totals.this_week_count ? "text-amber-300" : "text-muted-foreground/50"}`}>
                      {payPlan.totals.this_week_count ? audFull(payPlan.totals.this_week_total) : "—"}
                    </p>
                  </div>
                  <div className={`rounded-lg border px-2 py-1.5 text-center ${payPlan.totals.this_month_count ? "border-orange-500/30 bg-orange-500/[0.07]" : "border-border/60 bg-muted/20"}`} data-testid="dashboard-payplan-month">
                    <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Within 30 days</p>
                    <p className={`text-xs font-semibold tabular-nums ${payPlan.totals.this_month_count ? "text-orange-300" : "text-muted-foreground/50"}`}>
                      {payPlan.totals.this_month_count ? audFull(payPlan.totals.this_month_total) : "—"}
                    </p>
                  </div>
                </div>
                {(() => {
                  const urgent = [
                    ...(payPlan.buckets.breached || []),
                    ...(payPlan.buckets.this_week || []),
                    ...(payPlan.buckets.this_month || []),
                  ];
                  const list = urgent.length ? urgent : (payPlan.buckets.later || []);
                  return (
                    <>
                      {!urgent.length && (
                        <p className="text-[11px] text-emerald-300/90" data-testid="dashboard-payplan-clear">
                          Nothing is approaching 90 days — you&apos;re on top of the payables.
                        </p>
                      )}
                      {list.slice(0, 5).map((r) => (
                        <Link
                          key={`${r.kind}-${r.id}`}
                          to="/invoice-inbox?tab=unpaid"
                          className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-muted/60"
                          data-testid={`dashboard-payplan-row-${r.id}`}
                        >
                          <span className="min-w-0 flex-1 truncate text-muted-foreground">{r.supplier || "Supplier"}</span>
                          {r.days_overdue >= 90 ? (
                            <Badge variant="outline" className="border-red-500/30 bg-red-500/10 px-1 py-0 text-[9px] font-normal text-red-300">
                              {r.days_overdue}d overdue
                            </Badge>
                          ) : r.days_overdue > 0 ? (
                            <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 px-1 py-0 text-[9px] font-normal text-amber-300">
                              hits 90 in {r.days_to_90}d
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-border bg-muted/40 px-1 py-0 text-[9px] font-normal text-muted-foreground">
                              not yet due
                            </Badge>
                          )}
                          <span className="tabular-nums">{audFull(r.amount_due)}</span>
                        </Link>
                      ))}
                    </>
                  );
                })()}
                <p className="text-[11px] text-muted-foreground">
                  Ranked by how close each bill is to 90 days overdue — also in your Monday morning digest.
                </p>
              </CardContent>
            </Card>
          )}

          <Card className="shadow-sm" data-testid="dashboard-aging-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Receivables aging</CardTitle>
              <p className="text-xs text-muted-foreground">
                Outstanding <span className="font-semibold text-foreground tabular-nums">{audFull(aging?.total_outstanding || 0)}</span> inc GST
              </p>
            </CardHeader>
            <CardContent className="space-y-3 pt-1">
              {AGING_ROWS.map(({ key, label }, idx) => {
                const val = aging?.[key] || 0;
                const danger = idx >= 3 && val > 0;
                return (
                  <div key={key} data-testid={`aging-row-${key}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{label}</span>
                      <span className={`font-medium tabular-nums ${danger ? "text-red-300" : val > 0 && idx > 0 ? "text-amber-300" : ""}`}>
                        {audFull(val)}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full ${danger ? "bg-red-400/80" : idx === 0 ? "bg-primary/70" : "bg-amber-400/70"}`}
                        style={{ width: `${Math.min(100, (val / maxAging) * 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              <p className="pt-1 text-[11px] text-muted-foreground">
                Overdue invoices auto-remind clients every 3 days (max 3).
              </p>
            </CardContent>
          </Card>

        </div>
      </div>
      <EmailDevReportDialog job={emailReportJob} onOpenChange={(v) => !v && setEmailReportJob(null)} />
    </div>
  );
}
